import React, { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  TouchableOpacity,
  ScrollView,
  Pressable,
  Modal,
  Dimensions,
} from "react-native";

const { width: SCREEN_WIDTH } = Dimensions.get("window");
const TOOLTIP_WIDTH = 120;
import { FontFamilies } from "@/constants/Typography";
import { ActionButton } from "@/components/ActionButton";
import { router, Stack } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import Icons from "@/assets/icons";

interface CompanyIcon {
  icon: React.FC<{ width: number; height: number }>;
  name: string;
  claimAmount?: string;
}

interface Category {
  id: string;
  label: string;
  description: string;
  companies: CompanyIcon[];
  moreCount?: number;
  selected: boolean;
  expanded: boolean;
}

const INITIAL_CATEGORIES: Category[] = [
  {
    id: "false-advertising",
    label: "False advertising",
    description:
      "False advertising is when a company makes misleading or untrue claims about its products or services to encourage sales.",
    companies: [
      { icon: Icons.CompanyApple, name: "Apple", claimAmount: "Claim up to $80" },
      { icon: Icons.CompanyInstagram, name: "Instagram" },
      { icon: Icons.CompanyGoogle, name: "Google" },
      { icon: Icons.CompanyFacebook, name: "Facebook" },
      { icon: Icons.CompanyWordpress, name: "WordPress" },
      { icon: Icons.CompanyTiktok, name: "TikTok" },
    ],
    moreCount: 99,
    selected: false,
    expanded: false,
  },
  {
    id: "data-privacy",
    label: "Data privacy",
    description:
      "Data privacy cases involve companies mishandling, selling, or exposing your personal information without proper consent.",
    companies: [
      { icon: Icons.CompanyFacebook, name: "Facebook" },
      { icon: Icons.CompanyGoogle, name: "Google" },
      { icon: Icons.CompanyTiktok, name: "TikTok" },
    ],
    selected: false,
    expanded: false,
  },
  {
    id: "defective-products",
    label: "Defective products",
    description:
      "Defective product cases involve items that are dangerous, faulty, or don't work as advertised.",
    companies: [
      { icon: Icons.CompanyApple, name: "Apple" },
    ],
    selected: false,
    expanded: false,
  },
  {
    id: "all-other",
    label: "All other cases",
    description:
      "Other class action cases including employment disputes, securities fraud, and more.",
    companies: [],
    selected: false,
    expanded: false,
  },
];

const ClassActionsSelectScreen = () => {
  const [categories, setCategories] = useState<Category[]>(INITIAL_CATEGORIES);
  const [tooltipData, setTooltipData] = useState<{ text: string; x: number; y: number } | null>(null);

  const showTooltip = (text: string, event: any) => {
    const { pageX, pageY } = event.nativeEvent;
    // Calculate x position ensuring tooltip stays within screen bounds
    const padding = 16;
    let x = pageX - TOOLTIP_WIDTH / 2;
    if (x < padding) {
      x = padding;
    } else if (x + TOOLTIP_WIDTH > SCREEN_WIDTH - padding) {
      x = SCREEN_WIDTH - TOOLTIP_WIDTH - padding;
    }
    setTooltipData({ text, x, y: pageY - 45 });
  };

  const hideTooltip = () => {
    setTooltipData(null);
  };

  const toggleSelection = (id: string) => {
    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === id ? { ...cat, selected: !cat.selected } : cat
      )
    );
  };

  const toggleExpanded = (id: string) => {
    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === id ? { ...cat, expanded: !cat.expanded } : cat
      )
    );
  };

  const handleContinue = () => {
    router.push("/signup/unclaimed-money-search");
  };

  const handleSkip = () => {
    router.dismissTo("/(tabs)/feeds");
  };

  const renderCheckbox = (selected: boolean) => {
    if (selected) {
      return (
        <View style={styles.checkboxSelected}>
          <Ionicons name="checkmark" size={14} color="#0F9129" />
        </View>
      );
    }
    return <View style={styles.checkboxEmpty} />;
  };

  const renderCategory = (category: Category) => {
    return (
      <View key={category.id} style={styles.categoryContainer}>
        <TouchableOpacity
          style={styles.categoryHeader}
          onPress={() => toggleSelection(category.id)}
          activeOpacity={0.7}
        >
          {renderCheckbox(category.selected)}
          <Text style={styles.categoryLabel}>{category.label}</Text>
          <TouchableOpacity
            style={styles.expandButton}
            onPress={() => toggleExpanded(category.id)}
          >
            <Ionicons
              name={category.expanded ? "chevron-up" : "chevron-down"}
              size={20}
              color="#0F9129"
            />
          </TouchableOpacity>
        </TouchableOpacity>

        {category.expanded && (
          <View style={styles.categoryContent}>
            <Text style={styles.categoryDescription}>{category.description}</Text>
            {category.companies.length > 0 && (
              <View style={styles.companiesRow}>
                {category.companies.slice(0, 6).map((company, index) => {
                  if (company.claimAmount) {
                    return (
                      <TouchableOpacity
                        key={index}
                        style={styles.companyBadge}
                        onPress={(e) => showTooltip(company.claimAmount!, e)}
                        activeOpacity={0.7}
                      >
                        <company.icon width={12} height={12} />
                      </TouchableOpacity>
                    );
                  }

                  return (
                    <View key={index} style={styles.companyBadge}>
                      <company.icon width={12} height={12} />
                    </View>
                  );
                })}
                {category.moreCount && (
                  <View style={styles.companyMoreBadge}>
                    <Text style={styles.companyMoreText}>
                      +{category.moreCount}
                    </Text>
                  </View>
                )}
              </View>
            )}
          </View>
        )}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <Stack.Screen options={{ headerShown: false, gestureEnabled: false }} />
      <StatusBar barStyle="dark-content" />

      {/* Tooltip Modal */}
      <Modal
        visible={tooltipData !== null}
        transparent
        animationType="fade"
        onRequestClose={hideTooltip}
      >
        <Pressable style={styles.tooltipOverlay} onPress={hideTooltip}>
          {tooltipData && (
            <View
              style={[
                styles.tooltip,
                {
                  left: tooltipData.x,
                  top: tooltipData.y,
                },
              ]}
            >
              <Text style={styles.tooltipText}>{tooltipData.text}</Text>
            </View>
          )}
        </Pressable>
      </Modal>
      <SafeAreaView style={styles.safeArea}>
        {/* Progress Bar */}
        <View style={styles.progressContainer}>
          <View style={styles.progressBar}>
            <View style={styles.progressFill} />
          </View>
        </View>

        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Header */}
          <View style={styles.headerSection}>
            <Text style={styles.sectionLabel}>Class action claims</Text>
            <Text style={styles.title}>
              Choose class actions you care about
            </Text>
            <Text style={styles.subtitle}>
              Select categories to personalize your class action claim
              recommendations.
            </Text>
          </View>

          {/* Categories */}
          <View style={styles.categoriesList}>
            {categories.map((category) => renderCategory(category))}
          </View>
        </ScrollView>

        {/* Bottom Section */}
        <View style={styles.bottomSection}>
          {/* Privacy Notice */}
          <View style={styles.privacyNotice}>
            <Ionicons name="shield-checkmark" size={18} color="#0F9129" />
            <View style={styles.privacyTextContainer}>
              <Text style={styles.privacyText}>We do not sell your data.</Text>
              <Text style={styles.privacyText}>
                This is only required to perform the search.
              </Text>
            </View>
          </View>

          {/* Continue Button */}
          <View style={styles.buttonContainer}>
            <ActionButton title="Continue" onPress={handleContinue} />
          </View>

          {/* Skip Link */}
          <TouchableOpacity onPress={handleSkip}>
            <Text style={styles.skipText}>I'll do this later</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </View>
  );
};

export default ClassActionsSelectScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  safeArea: {
    flex: 1,
  },
  progressContainer: {
    paddingHorizontal: 24,
    paddingTop: 10,
    paddingBottom: 16,
  },
  progressBar: {
    height: 6,
    backgroundColor: "#E1EFE4",
    borderRadius: 100,
  },
  progressFill: {
    width: 16,
    height: 6,
    backgroundColor: "rgba(22, 29, 20, 0.5)",
    borderRadius: 100,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 24,
  },
  headerSection: {
    gap: 12,
    marginBottom: 24,
  },
  sectionLabel: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 16,
    lineHeight: 22,
    color: "#0F9129",
    letterSpacing: -0.32,
  },
  title: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 32,
    lineHeight: 38,
    color: "#000000",
    letterSpacing: -0.64,
  },
  subtitle: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 16,
    lineHeight: 24,
    color: "rgba(22, 29, 20, 0.7)",
    letterSpacing: -0.32,
  },
  categoriesList: {
    gap: 20,
  },
  categoryContainer: {
    gap: 8,
  },
  categoryHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  checkboxEmpty: {
    width: 18,
    height: 18,
    borderWidth: 1.4,
    borderColor: "#0F9129",
    borderRadius: 2,
  },
  checkboxSelected: {
    width: 18,
    height: 18,
    borderWidth: 1.4,
    borderColor: "#0F9129",
    borderRadius: 2,
    justifyContent: "center",
    alignItems: "center",
  },
  categoryLabel: {
    flex: 1,
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 15,
    lineHeight: 23,
    color: "#161D14",
  },
  expandButton: {
    width: 20,
    height: 20,
    borderRadius: 20,
    backgroundColor: "rgba(15, 145, 41, 0.1)",
    justifyContent: "center",
    alignItems: "center",
  },
  categoryContent: {
    paddingLeft: 30,
    gap: 8,
  },
  categoryDescription: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 13,
    lineHeight: 19,
    color: "#414141",
  },
  companiesRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
  companyBadge: {
    width: 20,
    height: 20,
    borderRadius: 4,
    backgroundColor: "#F3F3F3",
    justifyContent: "center",
    alignItems: "center",
  },
  tooltipOverlay: {
    flex: 1,
    backgroundColor: "transparent",
  },
  tooltip: {
    position: "absolute",
    backgroundColor: "#161D14",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    width: TOOLTIP_WIDTH,
    alignItems: "center",
  },
  tooltipText: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 12,
    color: "#FFFFFF",
    textAlign: "center",
  },
  companyMoreBadge: {
    height: 20,
    paddingHorizontal: 6,
    borderRadius: 4,
    backgroundColor: "#F3F3F3",
    justifyContent: "center",
    alignItems: "center",
  },
  companyMoreText: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 9,
    color: "#161D14",
  },
  bottomSection: {
    paddingHorizontal: 16,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "#F3F3F3",
    gap: 12,
  },
  privacyNotice: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 6,
  },
  privacyTextContainer: {
    flex: 1,
  },
  privacyText: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 14,
    lineHeight: 20,
    color: "#000000",
    letterSpacing: 0.14,
  },
  buttonContainer: {
    marginBottom: 0,
  },
  skipText: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 14,
    lineHeight: 20,
    color: "#0F9129",
    textAlign: "center",
    letterSpacing: -0.28,
    paddingBottom: 8,
  },
});
